import React, { useState, useEffect } from "react";
import {
  Building2,
  Phone,
  Mail,
  ShieldCheck,
  FileText,
  Landmark,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

/**
 * SuiteStudioBar — Fascia informativa aziendale Studio + Agenzia.
 *
 * Master Shell v2 · v1.11.0 (28/07/2026 · Brand Book v1.0)
 * ========================================================
 * Legge i dati da `studio_settings` (via prop `settings`).
 * 2 righe:
 *   Riga 1: STUDIO (Tel · Email · PEC · SDI) | AGENZIA (Tel · Email · PEC)
 *   Riga 2: REA · C.F. · P.IVA · Banca · IBAN
 * Comandi copia su Email, PEC, SDI, IBAN.
 *
 * Varianti responsive:
 *   - Desktop (≥1200px): fascia completa a 2 righe, max 85px.
 *   - Notebook/Tablet (<1200px): variante compatta con "Dati Studio ▾"
 *     che espande un pannello con la fascia completa.
 *
 * L'operatore può comprimere temporaneamente la fascia con la X e
 * riaprirla con un click. Preferenza persistita in localStorage.
 */
const KEY_COLLAPSED = "suite:studiobar-collapsed";

function readCollapsed() {
  try {
    return window.localStorage.getItem(KEY_COLLAPSED) === "true";
  } catch {
    return false;
  }
}
function writeCollapsed(v) {
  try {
    window.localStorage.setItem(KEY_COLLAPSED, v ? "true" : "false");
  } catch {
    /* ignore */
  }
}

export function SuiteStudioBar({ settings, className }) {
  const [viewport, setViewport] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1440));
  const [collapsed, setCollapsedState] = useState(readCollapsed);
  const [expandedPanel, setExpandedPanel] = useState(false);

  useEffect(() => {
    const onResize = () => setViewport(window.innerWidth);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setCollapsed = (v) => {
    setCollapsedState(v);
    writeCollapsed(v);
  };

  // Studio_settings può usare vari alias — leggo tutti i campi utili
  const s = settings || {};
  const studio = {
    ragioneSociale: s.studioName || s.ragioneSociale || s.ragione_sociale || s.company_name || s.studio_name || "Gruppo Impresa Srl STP",
    tel: s.phoneStudio || s.telefonoStudio || s.studio_phone || s.telefono || s.phone || "—",
    email: s.emailStudio || s.studio_email || s.email || "—",
    pec: s.pecStudio || s.studio_pec || s.pec || "—",
    sdi: s.sdiCodeStudio || s.codiceSDI || s.sdi_code || s.sdi || s.codice_sdi || "—",
    agenziaTel: s.phoneAgenzia || s.telefonoAgenzia || s.agency_phone || null,
    agenziaEmail: s.emailAgenzia || s.agency_email || null,
    agenziaPec: s.pecAgenzia || s.agency_pec || null,
    rea: s.reaNumber || s.rea || s.rea_code || "—",
    cf: s.fiscalCode || s.codiceFiscale || s.codice_fiscale || s.fiscal_code || "—",
    piva: s.vatNumber || s.partitaIva || s.partita_iva || s.vat_number || "—",
    banca: s.bankName || s.banca || s.bank_name || s.bank || "—",
    iban: s.iban || "—",
  };
  const hasAgenzia = Boolean(studio.agenziaTel || studio.agenziaEmail || studio.agenziaPec);

  // Se compresso, mostra una barra minima con tasto per riaprire
  if (collapsed) {
    return (
      <div
        className={cn("shrink-0", className)}
        style={{ padding: "6px 20px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 8 }}
        data-testid="suite-studiobar-collapsed"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: "var(--gis-green)", backgroundColor: "rgba(11,107,79,0.08)", border: "none", borderRadius: 6, cursor: "pointer" }}
          data-testid="suite-studiobar-reopen"
        >
          <Building2 size={12} /> Dati Studio
        </button>
      </div>
    );
  }

  // Desktop: fascia completa
  if (viewport >= 1200) {
    return (
      <div
        className={cn("shrink-0", className)}
        style={{ padding: "8px 20px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #E5E7EB", display: "flex", flexDirection: "column", gap: 4, maxHeight: 85, overflow: "hidden" }}
        data-testid="suite-studiobar-full"
      >
        <div style={{ display: "grid", gridTemplateColumns: hasAgenzia ? "minmax(0, 1fr) 1px minmax(0, 1fr) auto" : "minmax(0, 1fr) auto", gap: 16, alignItems: "center" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
            <StudioBadge label="STUDIO" color="var(--gis-green)" />
            <StudioField icon={Phone} label="Tel" value={studio.tel} />
            <StudioField icon={Mail} label="Email" value={studio.email} copyable />
            <StudioField icon={ShieldCheck} label="PEC" value={studio.pec} copyable />
            <StudioField icon={FileText} label="SDI" value={studio.sdi} copyable mono />
          </div>
          {hasAgenzia && (
            <>
              <div style={{ width: 1, height: 22, backgroundColor: "#E5E7EB", justifySelf: "center" }} />
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 12px", minWidth: 0 }}>
                <StudioBadge label="AGENZIA" color="#F28C28" />
                {studio.agenziaTel && <StudioField icon={Phone} label="Tel" value={studio.agenziaTel} />}
                {studio.agenziaEmail && <StudioField icon={Mail} label="Email" value={studio.agenziaEmail} copyable />}
                {studio.agenziaPec && <StudioField icon={ShieldCheck} label="PEC" value={studio.agenziaPec} copyable />}
              </div>
            </>
          )}
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            title="Comprimi fascia"
            data-testid="suite-studiobar-collapse"
            style={{ width: 22, height: 22, background: "transparent", border: "none", color: "#6B7280", cursor: "pointer" }}
          >
            <X size={13} />
          </button>
        </div>
        <div style={{ height: 1, backgroundColor: "#E5E7EB", margin: "2px 0" }} />
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", minWidth: 0 }}>
          <StudioField label="REA" value={studio.rea} />
          <StudioField label="C.F." value={studio.cf} mono />
          <StudioField label="P.IVA" value={studio.piva} mono />
          <StudioField icon={Landmark} label="Banca" value={studio.banca} />
          <StudioField label="IBAN" value={studio.iban} copyable mono />
        </div>
      </div>
    );
  }

  // Notebook/Tablet: compact + expandable panel
  return (
    <>
      <div
        className={cn("shrink-0", className)}
        style={{ padding: "8px 20px", backgroundColor: "#FFFFFF", borderBottom: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: "#6B7280" }}
        data-testid="suite-studiobar-compact"
      >
        <Building2 size={13} color="var(--gis-green)" />
        <span style={{ fontWeight: 600, color: "#1F2A24", whiteSpace: "nowrap" }}>{studio.ragioneSociale}</span>
        <span>·</span>
        <span style={{ whiteSpace: "nowrap" }}>P.IVA {studio.piva}</span>
        <button
          type="button"
          onClick={() => setExpandedPanel((v) => !v)}
          data-testid="suite-studiobar-expand"
          style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", fontSize: 11, fontWeight: 600, color: "var(--gis-green)", backgroundColor: "rgba(11,107,79,0.08)", border: "none", borderRadius: 6, cursor: "pointer" }}
        >
          Dati Studio {expandedPanel ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
      </div>
      {expandedPanel && (
        <div
          style={{ padding: "10px 20px", backgroundColor: "#FAFBFC", borderBottom: "1px solid #E5E7EB", display: "flex", flexDirection: "column", gap: 8 }}
          data-testid="suite-studiobar-panel"
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
            <StudioBadge label="STUDIO" color="var(--gis-green)" />
            <StudioField icon={Phone} label="Tel" value={studio.tel} />
            <StudioField icon={Mail} label="Email" value={studio.email} copyable />
            <StudioField icon={ShieldCheck} label="PEC" value={studio.pec} copyable />
            <StudioField icon={FileText} label="SDI" value={studio.sdi} copyable mono />
          </div>
          {hasAgenzia && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
              <StudioBadge label="AGENZIA" color="#F28C28" />
              {studio.agenziaTel && <StudioField icon={Phone} label="Tel" value={studio.agenziaTel} />}
              {studio.agenziaEmail && <StudioField icon={Mail} label="Email" value={studio.agenziaEmail} copyable />}
              {studio.agenziaPec && <StudioField icon={ShieldCheck} label="PEC" value={studio.agenziaPec} copyable />}
            </div>
          )}
          <div style={{ height: 1, backgroundColor: "#E5E7EB" }} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
            <StudioField label="REA" value={studio.rea} />
            <StudioField label="C.F." value={studio.cf} mono />
            <StudioField label="P.IVA" value={studio.piva} mono />
            <StudioField icon={Landmark} label="Banca" value={studio.banca} />
            <StudioField label="IBAN" value={studio.iban} copyable mono />
          </div>
        </div>
      )}
    </>
  );
}

function StudioBadge({ label, color }) {
  return (
    <span
      style={{
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: "#fff",
        backgroundColor: color,
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
  );
}

function StudioField({ icon: Icon, label, value, copyable, mono }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    if (!value || value === "—") return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copiato negli appunti`);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error("Copia non riuscita");
    }
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0, flexShrink: 0 }}>
      {Icon && <Icon size={12} color="#9CA3AF" />}
      <span style={{ color: "#9CA3AF", fontSize: 11, fontWeight: 500 }}>{label}:</span>
      <span style={{ color: "#1F2A24", fontSize: 12, fontFamily: mono ? "ui-monospace, SFMono-Regular, monospace" : "var(--gis-font-body)", fontWeight: 500, whiteSpace: "nowrap" }}>
        {value}
      </span>
      {copyable && value && value !== "—" && (
        <button
          type="button"
          onClick={doCopy}
          title={`Copia ${label}`}
          data-testid={`studiobar-copy-${label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
          style={{ marginLeft: 2, padding: 2, background: "transparent", border: "none", color: copied ? "var(--gis-green)" : "#9CA3AF", cursor: "pointer", display: "inline-flex" }}
        >
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>
      )}
    </span>
  );
}

export default SuiteStudioBar;
